import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock, ArrowRight } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email.trim(), password);
      toast.success("Welcome back");
      navigate(location.state?.from || "/admin", { replace: true });
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-cream">
      <div className="hidden lg:block relative bg-ink">
        <img
          src="https://images.unsplash.com/photo-1595751866979-de6e9d606220?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="Drunk Fruit bar"
          className="absolute inset-0 h-full w-full object-cover opacity-40"
        />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <img src="/logo-white.png" alt="Drunk Fruit" className="h-16 w-auto" />
          <div>
            <h1 className="font-serif text-5xl leading-tight mb-4">Event OS</h1>
            <p className="text-white/70 max-w-sm">Your complete operating system for quotes, contracts, menus, payments and automations — built for Drunk Fruit Mobile Bar.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="lg:hidden mb-8 flex justify-center">
            <Logo imgClassName="h-14 w-auto" />
          </div>
          <p className="eyebrow mb-2">Admin Portal</p>
          <h2 className="font-serif text-4xl mb-8">Sign in</h2>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label className="df-label" htmlFor="email">Email</Label>
              <Input
                id="email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="events@drunkfruit-ca.com"
                className="df-input" data-testid="login-email-input"
              />
            </div>
            <div>
              <Label className="df-label" htmlFor="password">Password</Label>
              <Input
                id="password" type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="df-input" data-testid="login-password-input"
              />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading} className="btn-dark w-full h-12" data-testid="login-submit-button">
              {loading ? "Signing in…" : "Sign In"} <ArrowRight className="h-4 w-4" />
            </Button>
          </form>

          <a href="/" className="mt-8 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-ink transition-colors" data-testid="back-to-site-link">
            ← Back to website
          </a>
          <p className="mt-10 flex items-center gap-2 text-xs text-muted-foreground"><Lock className="h-3 w-3" /> Secure staff access only</p>
        </div>
      </div>
    </div>
  );
}
